import React, { useRef, useState, useEffect } from 'react';
import { SEOLink } from './SEOLink';
import { Linkedin, ArrowUpRight } from 'lucide-react';

// LinkedIn social proof (E-E-A-T). Iframes load only when near viewport (no early
// third-party JS), fixed dimensions reserved to prevent CLS. Schema untouched —
// Organization/founder Person + LinkedIn sameAs already exist in the global graph.

const POSTS = [
  { urn: 'urn:li:ugcPost:7494031180229410817', collapsed: false, height: 1069 },
  { urn: 'urn:li:ugcPost:7494025885017227264', collapsed: true, height: 628 },
];

const LinkedInProof = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !('IntersectionObserver' in window)) { setVisible(true); return; }
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) { setVisible(true); io.disconnect(); }
    }, { rootMargin: '400px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-16" data-testid="linkedin-proof-section" aria-labelledby="linkedin-proof-heading">
      <div className="max-w-6xl mx-auto px-4">
        <div className="max-w-3xl mb-10">
          <div className="inline-flex items-center gap-2 glass px-4 py-2 rounded-full neo-shadow-sm mb-4">
            <Linkedin className="w-4 h-4 text-[#0A66C2]" />
            <span className="text-sm font-semibold text-slate-700">Company Updates</span>
          </div>
          <h2 id="linkedin-proof-heading" className="text-3xl md:text-4xl font-bold text-slate-800 mb-3">Zaroori Retail on LinkedIn</h2>
          <p className="text-slate-600">
            Follow Zaroori Retail and Deepinder Ahluwalia on LinkedIn for marketplace insights, e-commerce
            updates, brand success stories, company milestones and the latest developments across India's
            premium marketplace ecosystem.
          </p>
          <p className="text-slate-500 text-sm mt-3">
            From <SEOLink to="/services/myntra" className="text-red-600 underline">Myntra onboarding</SEOLink> and{' '}
            <SEOLink to="/services/nykaa" className="text-red-600 underline">Nykaa launches</SEOLink> to quick-commerce
            growth on <SEOLink to="/services/blinkit" className="text-red-600 underline">Blinkit</SEOLink> and{' '}
            <SEOLink to="/services/zepto" className="text-red-600 underline">Zepto</SEOLink>, our team shares what's
            actually working for brands — alongside lessons from{' '}
            <SEOLink to="/myntra-account-management" className="text-red-600 underline">marketplace account management</SEOLink>{' '}
            across 4200+ onboarded brands.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 justify-items-center md:justify-items-start">
          {POSTS.map((p, i) => (
            <div key={p.urn} className="w-full rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden" style={{ maxWidth: 504 }} data-testid={`linkedin-post-${i + 1}`}>
              {visible ? (
                <iframe
                  src={`https://www.linkedin.com/embed/feed/update/${p.urn}${p.collapsed ? '?collapsed=1' : ''}`}
                  height={p.height}
                  loading="lazy"
                  frameBorder="0"
                  allowFullScreen
                  title="Zaroori Retail LinkedIn post"
                  className="w-full block"
                  style={{ maxWidth: 504, height: p.height }}
                />
              ) : (
                <div style={{ height: p.height }} className="flex items-center justify-center bg-slate-50" aria-hidden="true">
                  <Linkedin className="w-8 h-8 text-slate-300" />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Always-visible fallback CTA (works even if LinkedIn embeds fail) */}
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="https://www.linkedin.com/company/zarooriretail" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#0A66C2] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#084d92] transition-colors"
            data-testid="linkedin-follow-company">
            <Linkedin className="w-4 h-4" /> Follow Zaroori Retail on LinkedIn <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
          <a href="https://www.linkedin.com/in/deepinder-ahluwalia/" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-slate-300 text-slate-700 px-5 py-2.5 rounded-full text-sm font-semibold hover:border-[#0A66C2] hover:text-[#0A66C2] transition-colors"
            data-testid="linkedin-follow-founder">
            Deepinder Ahluwalia — Founder & CEO <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default LinkedInProof;
